// src/SupportChatWidget.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { io } from 'socket.io-client';
// Берём origin (без /api), чтобы правильно показывать картинки /uploads/...
const API_ORIGIN = (
  process.env.REACT_APP_API_URL ||
  (typeof window !== 'undefined' ? window.location.origin : '')
).replace(/\/+$/, '');

// API base (/api)
const API_BASE = API_ORIGIN ? `${API_ORIGIN}/api` : '/api';

/**
 * Виджет чата поддержки для игрока.
 * Бэкенд:
 *  GET  /api/support/my-messages                   -> { threadId, status, messages: [...] }
 *  POST /api/support/upload   (FormData image)     -> { ok: true, url: "/uploads/support/xxx.jpg" }
 *  POST /api/support/send     { text?, imageUrl? } -> { ok: true, message: {...} }
 *
 * Socket:
 *  'support:message' -> { threadId, message }
 *
 * Сообщения с бэка: SupportMessage (Sequelize)
 *  { id, text, from_admin, created_at / createdAt, Author? }
 *
 * Картинка внутри text хранится строкой: "[img]/uploads/support/xxx.jpg"
 */
function SupportChatWidget({ username, token }) {
  const { t: translate, i18n } = useTranslation();
  const authToken = token || localStorage.getItem('token');

  const languageCode = String(
    i18n.resolvedLanguage || i18n.language || 'ru'
  )
    .split('-')[0]
    .toLowerCase();

  const dateLocale = {
    ru: 'ru-RU',
    en: 'en-GB',
    ka: 'ka-GE',
    hy: 'hy-AM',
    az: 'az-AZ',
    it: 'it-IT',
    fr: 'fr-FR',
  }[languageCode] || 'ru-RU';

  const [isOpen, setIsOpen] = useState(false);



const [unread, setUnread] = useState(() => {
  const v = Number(localStorage.getItem('support_unread') || 0);
  return Number.isFinite(v) ? v : 0;
});

const isOpenRef = useRef(false);
useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);



  const [threadId, setThreadId] = useState(null);
  const [messages, setMessages] = useState([]);

  const [input, setInput] = useState('');
  const [attachedImageUrl, setAttachedImageUrl] = useState(''); // url из /support/upload

  const [initialLoaded, setInitialLoaded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);

  const listRef = useRef(null);
  const fileInputRef = useRef(null);

  // ✅ сокет
  const socketRef = useRef(null);

  // --- helpers ---
  const resolveUrl = (pathOrUrl) => {
    if (!pathOrUrl) return '';
    const s = String(pathOrUrl);
    if (s.startsWith('http://') || s.startsWith('https://')) return s;
    // относительный путь /uploads/... -> добавим origin
    return API_ORIGIN ? `${API_ORIGIN}${s}` : s;
  };

  const shouldStickToBottomRef = useRef(true);

  const scrollToBottom = () => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    shouldStickToBottomRef.current = true;
  };

  const handleMessagesScroll = () => {
    const el = listRef.current;
    if (!el) return;

    const distanceToBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight;

    shouldStickToBottomRef.current = distanceToBottom < 80;
  };

  // Парсим "[img]..."
  const parseMessageText = (raw) => {
    const text = String(raw || '');
    const lines = text.split('\n');

    const imageUrls = [];
    const textLines = [];

    for (const line of lines) {
      const t = String(line || '').trim();
      if (t.startsWith('[img]')) {
        const url = t.slice(5).trim();
        if (url) imageUrls.push(url);
      } else {
        textLines.push(line);
      }
    }

    const textOnly = textLines.join('\n').trim();
    return { textOnly, imageUrls };
  };

  // Аккуратно добавляем сообщение, не ломая ручную прокрутку истории.
  const appendMessageSafe = (msg, { forceBottom = false } = {}) => {
    if (!msg) return;

    const shouldScroll =
      forceBottom || shouldStickToBottomRef.current;

    setMessages((prev) => {
      if (prev.some((x) => x.id === msg.id)) return prev;
      return [...prev, msg];
    });

    if (shouldScroll) {
      window.setTimeout(scrollToBottom, 30);
    }
  };




  async function markMessagesRead() {
    if (!authToken) return;

    try {
      const res = await fetch(`${API_BASE}/support/read`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (!res.ok) return;

      setUnread(0);
      localStorage.setItem('support_unread', '0');
    } catch (e) {
      console.warn('[Support] markMessagesRead error:', e);
    }
  }

  async function loadUnreadCount() {
    if (!authToken) return;

    try {
      const res = await fetch(`${API_BASE}/support/unread`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok) return;

      if (json?.threadId) {
        setThreadId((current) => current || json.threadId);
      }

      const nextUnread = Number(json?.unread || 0);
      setUnread(nextUnread);
      localStorage.setItem('support_unread', String(nextUnread));
    } catch (e) {
      console.warn('[Support] loadUnreadCount error:', e);
    }
  }

  function openChat() {
    shouldStickToBottomRef.current = true;
    setIsOpen(true);
  }

  function closeChat() {
    setIsOpen(false);
  }


  async function loadThread({ silent = false } = {}) {
    if (!authToken || !username) return;

    try {
      if (!silent) setRefreshing(true);

      const res = await fetch(`${API_BASE}/support/my-messages`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        console.warn('[Support] loadThread error:', json);
        return;
      }

      setThreadId(json.threadId || null);

      const msgs = Array.isArray(json.messages) ? json.messages : [];
      setMessages(msgs);

      setUnread(0);
      localStorage.setItem('support_unread', '0');
      shouldStickToBottomRef.current = true;

      if (!initialLoaded) setInitialLoaded(true);

      window.setTimeout(scrollToBottom, 50);
    } catch (e) {
      console.warn('[Support] loadThread exception:', e);
    } finally {
      if (!silent) setRefreshing(false);
    }
  }

  // ✅ 1) Подключаем сокет (один раз на authToken)
  useEffect(() => {
    // нет токена — нет сокета
    if (!authToken) return;

    // если уже подключали — не подключаем заново
    if (socketRef.current) return;

    const s = io(API_ORIGIN, {
      auth: { token: authToken },
      transports: ['websocket'],
    });

    socketRef.current = s;

    s.on('connect', () => {
      // можно логировать если нужно
      // console.log('[Support socket] connected', s.id);
    });

    // Новые сообщения поддержки.
    s.on('support:message', (payload) => {
      const msg = payload?.message;
      const tid = payload?.threadId;

      if (tid) {
        setThreadId((current) => current || tid);
      }

      appendMessageSafe(msg);

      const fromAdmin = !!msg?.from_admin;
      if (!fromAdmin) return;

      if (isOpenRef.current) {
        // Окно уже открыто: сообщение считается увиденным.
        window.setTimeout(() => {
          markMessagesRead();
        }, 50);
        return;
      }

      const countFromServer = Number(payload?.unreadForUser);

      setUnread((current) => {
        const next = Number.isFinite(countFromServer)
          ? countFromServer
          : current + 1;

        localStorage.setItem('support_unread', String(next));
        return next;
      });
    });


    s.on('disconnect', () => {
      // console.log('[Support socket] disconnected');
    });

    return () => {
      try {
        s.disconnect();
      } catch (e) {}
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);










 
  // После F5 восстанавливаем реальный непрочитанный счётчик из БД.
  useEffect(() => {
    if (!authToken) return;
    loadUnreadCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  // Когда открыли чат — грузим историю. GET /my-messages также фиксирует прочтение.
  useEffect(() => {
    if (!isOpen) return;
    if (!authToken || !username) return;

    loadThread({ silent: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, authToken, username]);

  // --- upload image ---
  async function uploadImage(file) {
    if (!file) return;
    if (!authToken || !username) return;

    try {
      setUploading(true);

      const fd = new FormData();
      fd.append('image', file);

      const res = await fetch(`${API_BASE}/support/upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          // ВАЖНО: Content-Type НЕ ставим вручную, иначе FormData сломается
        },
        body: fd,
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok || !json?.ok || !json?.url) {
        console.warn('[Support] upload error:', json);
        alert(json?.error || translate('support.uploadError'));
        return;
      }

      setAttachedImageUrl(String(json.url));
    } catch (e) {
      console.warn('[Support] upload exception:', e);
      alert(translate('support.uploadError'));
    } finally {
      setUploading(false);
    }
  }

  const onPickFile = () => {
    if (!authToken || !username) return;
    fileInputRef.current?.click();
  };

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    // чтобы можно было выбрать тот же файл ещё раз:
    e.target.value = '';
    if (!file) return;
    uploadImage(file);
  };

  // --- send ---
  const canSend = !!input.trim() || !!attachedImageUrl;

  const handleSend = async (e) => {
    e?.preventDefault();

    if (!canSend) return;
    if (!authToken || !username) return;

    const text = input.trim();

    try {
      setSending(true);

      const res = await fetch(`${API_BASE}/support/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          text: text || '',
          imageUrl: attachedImageUrl || '',
        }),
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok || !json?.ok) {
        console.warn('[Support] send error:', json);
        alert(json?.error || translate('support.sendError'));
        return;
      }

      // очистить поля
      setInput('');
      setAttachedImageUrl('');

      // ✅ ВАЖНО: НЕ loadThread(), чтобы не “дёргать” список
      // Сервер обычно отдаёт { ok:true, message:{...} } — добавим сразу
      if (json?.message) {
        shouldStickToBottomRef.current = true;
        appendMessageSafe(json.message, { forceBottom: true });
      } else {
        // запасной вариант: если message не вернули, можно один раз обновить
        // (но лучше поправить бэк чтобы возвращал message)
        // await loadThread({ silent: true });
      }
    } catch (e2) {
      console.warn('[Support] send exception:', e2);
      alert(translate('support.sendError'));
    } finally {
      setSending(false);
    }
  };

  // --- render ---
  return (
    <div className={`support-chat-wrapper ${isOpen ? 'open' : ''}`}>
      {/* язычок */}
    <div
  className="support-chat-tab"
  onClick={() => (isOpen ? closeChat() : openChat())}
>
  <span className="support-tab-label">
    {isOpen ? translate('support.close') : translate('support.open')}
  </span>

  {!isOpen && unread > 0 && (
    <span className="support-badge">{unread > 99 ? '99+' : unread}</span>
  )}
</div>


      <div className="support-chat-body">
        <div className="support-chat-header">
          <span>{translate('support.title')}</span>
          <span className="support-chat-status">
            {sending
              ? translate('support.sending')
              : refreshing
                ? translate('support.refreshing')
                : translate('support.online')}
          </span>
        </div>

        <div
          className="support-chat-messages"
          ref={listRef}
          onScroll={handleMessagesScroll}
        >
          {messages.map((m) => {
            const fromAdmin = !!m.from_admin;
            const created = m.created_at || m.createdAt || null;
            const { textOnly, imageUrls } = parseMessageText(m.text);

            return (
              <div
                key={m.id}
                className={'support-msg ' + (fromAdmin ? 'admin' : 'me')}
              >
                {/* текст */}
                {textOnly ? (
                  <div style={{ whiteSpace: 'pre-wrap' }}>{textOnly}</div>
                ) : null}

                {/* картинки */}
                {imageUrls.map((u, idx) => {
                  const src = resolveUrl(u);
                  return (
                    <a
                      key={`${m.id}-img-${idx}`}
                      href={src}
                      target="_blank"
                      rel="noreferrer"
                      style={{ display: 'inline-block', marginTop: 6 }}
                    >
                      <img
                        src={src}
                        alt={translate('support.messageImageAlt')}
                        style={{
                          maxWidth: 240,
                          maxHeight: 240,
                          borderRadius: 8,
                          display: 'block',
                        }}
                      />
                    </a>
                  );
                })}

                <div className="support-msg-meta">
                  {fromAdmin ? translate('support.sender') : username}
                  {created ? ' · ' + new Date(created).toLocaleString(dateLocale) : ''}
                </div>
              </div>
            );
          })}

          {!messages.length && !refreshing && (
            <div style={{ opacity: 0.7, fontSize: 12 }}>
              {translate('support.empty')}
            </div>
          )}
        </div>

        {/* предпросмотр прикреплённой картинки */}
        {attachedImageUrl && (
          <div style={{ padding: '6px 8px', borderTop: '1px solid #333' }}>
            <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>
              {translate('support.attached')}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <img
                src={resolveUrl(attachedImageUrl)}
                alt={translate('support.previewAlt')}
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 8,
                  objectFit: 'cover',
                }}
              />
              <button
                type="button"
                onClick={() => setAttachedImageUrl('')}
                style={{
                  cursor: 'pointer',
                  padding: '6px 10px',
                  borderRadius: 6,
                  border: '1px solid #444',
                  background: 'transparent',
                  color: 'inherit',
                }}
              >
                {translate('support.remove')}
              </button>
            </div>
          </div>
        )}

        {/* input row */}
        <form className="support-chat-input-row" onSubmit={handleSend}>
          {/* скрытый input файла */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            style={{ display: 'none' }}
            onChange={onFileChange}
          />

          <button
            type="button"
            onClick={onPickFile}
            disabled={uploading || sending || !authToken || !username}
            title={translate('support.attachImage')}
            style={{ width: 40 }}
          >
            {uploading ? '…' : '🖼️'}
          </button>

          <input
            type="text"
            placeholder={
              authToken && username
                ? translate('support.placeholder')
                : translate('support.loginRequired')
            }
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={!authToken || !username || sending}
          />

          <button
            type="submit"
            disabled={!canSend || sending || uploading || !authToken || !username}
          >
            ➤
          </button>
        </form>
      </div>
    </div>
  );
}

export default SupportChatWidget;
